import {IBackendCaller} from "./backendCaller";
import EventDetails from "../stores/domainStore/pages/models/eventDetails";
import EventBooking from "../stores/domainStore/pages/models/eventBooking";
import {EventCalendarType} from "../shared/enums/eventCalendarType";

const eventsDefaultUrl = "/api/events";
const eventBookingsDefaultUrl = "/api/eventBookings";
const multipleEventBookingsDefaultUrl = "/api/multipleEventBookings";

export interface IEventService {
  getAllEvents: (
    startDate: Date,
    endDate: Date,
    villageId?: string,
    personType?: string,
    includeFullDetails?: boolean
  ) => Promise<any>;
  getEvent: (
    id: string,
    occurrenceDate?: string,
    villageId?: string
  ) => Promise<any>;
  getEventOccurrenceInfos: (
    id: string,
    fromDate: string,
    withBookingsOnly?: boolean
  ) => Promise<any>;
  saveEvent: (eventDetails: EventDetails) => Promise<any>;
  getAllEventCategories: (calendarType?: EventCalendarType) => Promise<any>;
  getAllEventSharedLocations: () => Promise<any>;
  updateEvent: (
    eventDetails: EventDetails,
    updateRecurringEventOption?: string,
    occurrenceDate?: string
  ) => Promise<any>;
  deleteEvent: (
    eventDetails: EventDetails,
    occurrenceDate?: string,
    fromOccurrenceDate?: string
  ) => Promise<any>;
  cancelPostponeEvent: (
    eventDetails: EventDetails,
    updateRecurringEventOption?: string,
    occurrenceDate?: string
  ) => Promise<any>;

  /**
   * Gets all the bookings for a given event.
   */
  getEventBookings: (
    eventId: string,
    villageId: string,
    occurrenceDate?: string,
    status?: string
  ) => Promise<any>;
  /**
   * Updates an event booking.
   */
  updateEventBooking: (eventBooking: EventBooking) => Promise<any>;
  /**
   * Update event bookings.
   */
  updateMultipleEventBookings: (eventBookings: EventBooking[]) => Promise<any>;
  /**
   * Creates an event booking.
   */
  createEventBooking: (
    eventId: string,
    eventBooking: EventBooking,
    occurrenceDate?: string
  ) => Promise<any>;

  /**
   * Get categorised events.
   */
  getCategorisedEvents: (
    calendarType: EventCalendarType,
    startDate: Date,
    endDate: Date,
    villageId: string,
    personType: string
  ) => Promise<any>;
}

export class EventService implements IEventService {
  backendCaller: IBackendCaller;

  constructor(backendCaller: IBackendCaller) {
    this.backendCaller = backendCaller;
  }

  saveEvent = async (eventDetails: EventDetails) => {
    return await this.backendCaller.callApi(
      "POST",
      eventsDefaultUrl,
      JSON.stringify(eventDetails.requestObject)
    );
  };

  updateEvent = async (
    eventDetails: EventDetails,
    updateRecurringEventOption?: string,
    occurrenceDate?: string
  ) => {
    let eventDetailUrl = `${eventsDefaultUrl}/${eventDetails.id}`;

    if (updateRecurringEventOption) {
      eventDetailUrl += `?recurringEventOption=${updateRecurringEventOption}`;
    }

    if (updateRecurringEventOption && occurrenceDate) {
      const encodedOccurrenceDate = encodeURIComponent(occurrenceDate);
      eventDetailUrl += `&occurrenceDate=${encodedOccurrenceDate}`;
    }

    return await this.backendCaller.callApi(
      "PUT",
      eventDetailUrl,
      JSON.stringify(eventDetails.requestObject)
    );
  };

  deleteEvent = async (
    eventDetails: EventDetails,
    occurrenceDate?: string,
    fromOccurrenceDate?: string
  ) => {
    let eventDetailUrl = `${eventsDefaultUrl}/${eventDetails.id}`;

    if (occurrenceDate) {
      const encodedOccurrenceDate = encodeURIComponent(occurrenceDate);
      eventDetailUrl += `?occurrenceDate=${encodedOccurrenceDate}`;
    }

    if (fromOccurrenceDate) {
      const encodedFromOccurrenceDate = encodeURIComponent(fromOccurrenceDate);
      eventDetailUrl += `?fromOccurrenceDate=${encodedFromOccurrenceDate}`;
    }

    return await this.backendCaller.callApi("DELETE", `${eventDetailUrl}`);
  };

  cancelPostponeEvent = async (
    eventDetails: EventDetails,
    updateRecurringEventOption?: string,
    occurrenceDate?: string
  ) => {
    let eventDetailUrl = `${eventsDefaultUrl}/${eventDetails.id}/cancpone`;

    if (updateRecurringEventOption) {
      eventDetailUrl += `?recurringEventOption=${updateRecurringEventOption}`;
    }

    if (updateRecurringEventOption && occurrenceDate) {
      const encodedOccurrenceDate = encodeURIComponent(occurrenceDate);
      eventDetailUrl += `&occurrenceDate=${encodedOccurrenceDate}`;
    }

    return await this.backendCaller.callApi(
      "PUT",
      `${eventDetailUrl}`,
      JSON.stringify(eventDetails.requestObject)
    );
  };

  getAllEvents = async (
    startDate: Date,
    endDate: Date,
    villageId?: string,
    personType?: string,
    includeFullDetails?: boolean
  ) => {
    startDate.setMilliseconds(0);
    endDate.setMilliseconds(0);
    let uri = `${eventsDefaultUrl}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&villageId=${villageId}&personType=${personType}`;
    if (includeFullDetails) {
      uri += "&includeFullDetails=full";
    }
    return await this.backendCaller.callApi("GET", uri, null);
  };

  getEvent = async (
    id: string,
    occurrenceDate?: string,
    villageId?: string
  ) => {
    let eventDetailUrl = `${eventsDefaultUrl}/${id}`;

    if (occurrenceDate) {
      const encodedOccurrenceDate = encodeURIComponent(occurrenceDate);
      eventDetailUrl += `?occurrenceDate=${encodedOccurrenceDate}`;
    }

    if (villageId) {
      if (occurrenceDate) {
        eventDetailUrl += `&villageId=${villageId}`;
      } else {
        eventDetailUrl += `?villageId=${villageId}`;
      }
    }

    return await this.backendCaller.callApi("GET", eventDetailUrl, null);
  };

  getEventOccurrenceInfos = (
    id: string,
    fromDate: string,
    withBookingsOnly?: boolean
  ) => {
    const encodedFromDate = encodeURIComponent(fromDate);
    let eventDetailUrl = `${eventsDefaultUrl}/${id}/eventOccurrences?fromDate=${encodedFromDate}`;

    if (withBookingsOnly != undefined) {
      eventDetailUrl += `&withBookingsOnly=${withBookingsOnly}`;
    }

    return this.backendCaller.callApi("GET", eventDetailUrl, null);
  };

  getAllEventCategories = async (
    calendarType: EventCalendarType = EventCalendarType.Enhanced
  ) => {
    return await this.backendCaller.callApi(
      "GET",
      `${eventsDefaultUrl}/categories?calendarType=${calendarType}`,
      null
    );
  };

  getAllEventSharedLocations = async () => {
    return await this.backendCaller.callApi(
      "GET",
      `${eventsDefaultUrl}/locations`,
      null
    );
  };

  getEventBookings = async (
    eventId: string,
    villageId: string,
    occurrenceDate?: string,
    status?: string | undefined
  ) => {
    let url = `${eventBookingsDefaultUrl}/${eventId}?villageId=${villageId}`;
    if (occurrenceDate) {
      const encodedOccurrenceDate = encodeURIComponent(occurrenceDate);
      url += `&occurrenceDate=${encodedOccurrenceDate}`;
    }
    if (status) {
      url += `&status=${status?.toLowerCase()}`;
    }

    return await this.backendCaller.callApi("GET", url, null);
  };

  updateEventBooking = async (eventBooking: EventBooking) => {
    return await this.backendCaller.callApi(
      "PUT",
      `${eventBookingsDefaultUrl}/${eventBooking.id}`,
      JSON.stringify(eventBooking.requestObject),
      null,
      true
    );
  };

  updateMultipleEventBookings = async (eventBookings: EventBooking[]) => {
    return await this.backendCaller.callApi(
      "PUT",
      `${multipleEventBookingsDefaultUrl}`,
      JSON.stringify({
        eventBookings: eventBookings.map((booking) => booking.requestObject)
      }),
      null,
      true
    );
  };

  createEventBooking = (
    eventId: string,
    eventBooking: EventBooking,
    occurrenceDate?: string
  ) => {
    let url = `${eventBookingsDefaultUrl}/${eventId}/bookings`;

    if (occurrenceDate) {
      const encodedOccurrenceDate = encodeURIComponent(occurrenceDate);
      url += `?occurrenceDate=${encodedOccurrenceDate}`;
    }

    return this.backendCaller.callApi(
      "POST",
      `${url}`,
      JSON.stringify(eventBooking.requestObject),
      null,
      true
    );
  };

  getCategorisedEvents = (
    calendarType: EventCalendarType,
    startDate: Date,
    endDate: Date,
    villageId: string,
    personType: string
  ) => {
    return this.backendCaller.callApi(
      "GET",
      `${eventsDefaultUrl}/categorieswithevents?calendarType=${calendarType}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&villageId=${villageId}&personType=${personType}`,
      null,
      null,
      true
    );
  };
}
